import { useCallback, useRef, useState } from 'react';
import * as api from '../liveSessionApi';
import { defaultPhaseSecsForStatus } from '../lib/roundClock';
import type {
  EndMatchResponse,
  LiveStateResponse,
  MatchRoundResponse,
  StrategySummary,
} from '../types';

export type StrategistActionKey =
  | 'strategy'
  | 'start'
  | 'end'
  | 'clock'
  | 'plant'
  | 'defuse'
  | 'reset'
  | 'createRound'
  | 'nextRound'
  | 'endMatch'
  | 'startingSide';

export type UseStrategistActionsArgs = {
  teamId: string;
  sessionId: string;
  matchId: string;
  liveState: LiveStateResponse | null;
  onSuccess: () => void;
};

export type UseStrategistActionsResult = {
  pendingAction: StrategistActionKey | null;
  actionError: string | null;
  pendingRoundId: string | null;
  pendingRoundNumber: number | null;
  selectedStrategyId: string | null;
  selectedStrategyName: string | null;
  selectedStrategySide: string | null;
  appliedStrategyId: string | null;
  strategyNeedsConfirm: boolean;
  canConfirmStrategy: boolean;
  selectStrategyDraft: (strategy: StrategySummary) => void;
  confirmStrategy: () => Promise<void>;
  start: () => Promise<void>;
  endWin: () => Promise<void>;
  endLoss: () => Promise<void>;
  setClock: (clockSecs: number) => Promise<void>;
  plant: () => Promise<void>;
  defuse: () => Promise<void>;
  resetClock: () => Promise<void>;
  nextRound: () => Promise<void>;
  endMatch: () => Promise<EndMatchResponse | null>;
  setStartingSide: (side: 'T' | 'CT') => Promise<void>;
  clearError: () => void;
};

export function useStrategistActions({
  teamId,
  sessionId,
  matchId,
  liveState,
  onSuccess,
}: UseStrategistActionsArgs): UseStrategistActionsResult {
  const [pendingAction, setPendingAction] = useState<StrategistActionKey | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingRoundId, setPendingRoundId] = useState<string | null>(null);
  const [pendingRoundNumber, setPendingRoundNumber] = useState<number | null>(null);
  const [selectedStrategyId, setSelectedStrategyId] = useState<string | null>(null);
  const [selectedStrategyName, setSelectedStrategyName] = useState<string | null>(null);
  const [selectedStrategySide, setSelectedStrategySide] = useState<string | null>(null);
  const [appliedStrategyId, setAppliedStrategyId] = useState<string | null>(null);
  const pendingRoundRef = useRef<string | null>(null);
  const selectedStrategyIdRef = useRef<string | null>(null);

  const clearError = useCallback(() => setActionError(null), []);

  const clearPrep = useCallback(() => {
    pendingRoundRef.current = null;
    setPendingRoundId(null);
    setPendingRoundNumber(null);
    selectedStrategyIdRef.current = null;
    setSelectedStrategyId(null);
    setSelectedStrategyName(null);
    setSelectedStrategySide(null);
    setAppliedStrategyId(null);
  }, []);

  const run = useCallback(
    async (key: StrategistActionKey, fn: () => Promise<unknown>) => {
      setPendingAction(key);
      setActionError(null);
      try {
        await fn();
        onSuccess();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : String(err));
      } finally {
        setPendingAction(null);
      }
    },
    [onSuccess],
  );

  const currentRoundId = liveState?.currentRound?.id ?? null;
  const currentStatus = (liveState?.currentRound?.status ?? '').toLowerCase() || null;
  const currentRoundNumber = liveState?.currentRound?.roundNumber ?? null;

  const strategyNeedsConfirm =
    selectedStrategyId != null && selectedStrategyId !== appliedStrategyId;
  // Server only rejects while a round is active (pending/buy can receive strategy).
  const canConfirmStrategy = currentStatus !== 'active';

  const rememberCreatedRound = useCallback((created: MatchRoundResponse) => {
    const id = created.id;
    if (!id) throw new Error('Create round did not return an id');
    pendingRoundRef.current = id;
    setPendingRoundId(id);
    if (created.roundNumber != null) {
      setPendingRoundNumber(created.roundNumber);
    } else if (currentRoundNumber != null) {
      setPendingRoundNumber(currentRoundNumber + 1);
    }
    return id;
  }, [currentRoundNumber]);

  const selectStrategyDraft = useCallback((strategy: StrategySummary) => {
    selectedStrategyIdRef.current = strategy.id;
    setSelectedStrategyId(strategy.id);
    setSelectedStrategyName(strategy.name);
    setSelectedStrategySide(strategy.side ?? null);
    setActionError(null);
  }, []);

  const confirmStrategy = useCallback(async () => {
    await run('strategy', async () => {
      const strategyId = selectedStrategyIdRef.current;
      if (!strategyId) throw new Error('No strategy selected');

      const status = (currentStatus ?? '').toLowerCase();
      if (status === 'active') {
        throw new Error('Finish the current round first');
      }

      // Match strategy requires a pending/buy preparable round.
      // When buy, set on current; when pending, set on current; otherwise create next.
      if (status !== 'pending' && status !== 'buy') {
        const created = await api.nextRound(teamId, sessionId, matchId);
        rememberCreatedRound(created);
      } else if (status === 'pending' && currentRoundId) {
        pendingRoundRef.current = currentRoundId;
        setPendingRoundId(currentRoundId);
        if (currentRoundNumber != null) setPendingRoundNumber(currentRoundNumber);
      }

      await api.setMatchStrategy(teamId, sessionId, matchId, strategyId);
      setAppliedStrategyId(strategyId);
      if (pendingRoundNumber == null && currentRoundNumber != null && status !== 'pending' && status !== 'buy') {
        setPendingRoundNumber(currentRoundNumber + 1);
      }
    });
  }, [
    run,
    teamId,
    sessionId,
    matchId,
    currentStatus,
    currentRoundId,
    currentRoundNumber,
    pendingRoundNumber,
    rememberCreatedRound,
  ]);

  const start = useCallback(async () => {
    await run('start', async () => {
      // Prefer starting the in-progress current round; if only prep exists, start that.
      const roundId = currentRoundId ?? pendingRoundRef.current;
      if (!roundId) throw new Error('No round to start');
      await api.startRound(teamId, sessionId, matchId, roundId);
      clearPrep();
    });
  }, [run, teamId, sessionId, matchId, currentRoundId, clearPrep]);

  const endWith = useCallback(
    async (result: 'win' | 'loss') => {
      await run('end', async () => {
        if (!currentRoundId) throw new Error('No current round to end');
        await api.endRound(teamId, sessionId, matchId, currentRoundId, result);
      });
    },
    [run, teamId, sessionId, matchId, currentRoundId],
  );

  const setClock = useCallback(
    async (clockSecs: number) => {
      await run('clock', async () => {
        if (!currentRoundId) throw new Error('No current round');
        await api.setRoundClock(teamId, sessionId, matchId, currentRoundId, clockSecs);
      });
    },
    [run, teamId, sessionId, matchId, currentRoundId],
  );

  const plant = useCallback(async () => {
    await run('plant', async () => {
      if (!currentRoundId) throw new Error('No current round');
      await api.confirmBombPlant(teamId, sessionId, matchId, currentRoundId);
    });
  }, [run, teamId, sessionId, matchId, currentRoundId]);

  const defuse = useCallback(async () => {
    await run('defuse', async () => {
      if (!currentRoundId) throw new Error('No current round');
      await api.confirmBombDefuse(teamId, sessionId, matchId, currentRoundId);
    });
  }, [run, teamId, sessionId, matchId, currentRoundId]);

  const resetClock = useCallback(async () => {
    await run('reset', async () => {
      if (!currentRoundId) throw new Error('No current round');
      const secs = defaultPhaseSecsForStatus(currentStatus);
      await api.setRoundClock(teamId, sessionId, matchId, currentRoundId, secs);
    });
  }, [run, teamId, sessionId, matchId, currentRoundId, currentStatus]);

  const nextRound = useCallback(async () => {
    await run('nextRound', async () => {
      const created = await api.nextRound(teamId, sessionId, matchId);
      rememberCreatedRound(created);
    });
  }, [run, teamId, sessionId, matchId, rememberCreatedRound]);

  const endMatch = useCallback(async (): Promise<EndMatchResponse | null> => {
    let result: EndMatchResponse | null = null;
    await run('endMatch', async () => {
      result = await api.endMatch(teamId, sessionId, matchId);
    });
    return result;
  }, [run, teamId, sessionId, matchId]);

  const setStartingSide = useCallback(
    async (side: 'T' | 'CT') => {
      await run('startingSide', async () => {
        await api.setStartingSide(teamId, sessionId, matchId, side);
      });
    },
    [run, teamId, sessionId, matchId],
  );

  return {
    pendingAction,
    actionError,
    pendingRoundId,
    pendingRoundNumber,
    selectedStrategyId,
    selectedStrategyName,
    selectedStrategySide,
    appliedStrategyId,
    strategyNeedsConfirm,
    canConfirmStrategy,
    selectStrategyDraft,
    confirmStrategy,
    start,
    endWin: () => endWith('win'),
    endLoss: () => endWith('loss'),
    setClock,
    plant,
    defuse,
    resetClock,
    nextRound,
    endMatch,
    setStartingSide,
    clearError,
  };
}

export type { MatchRoundResponse };
